import { CenterPage } from "Components/CenterPage/CenterPage";
import Loading from "Components/Loading/Loading";
import LightningLandersBuilder from "features/lightningLanders/components/LightningLandersBuilder";
import { useSelectedProject } from "Hooks";
import produce from "immer";
import { useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { saveComponent, readComponent } from "features/campaigns";
import { friendlyDateTime } from "Helpers/dates";
import LightningLanderProvider, {
  useLightningLanderContext,
} from "features/lightningLanders/providers/LightningLanderProvider";
import {
  ApiComponentType,
  ApiProjectSettingKey,
  ApiSimpleComponent,
} from "@incendium/api";
import {
  landerDefaultTheme,
  LightningLanderThemeProvider,
  useLightningLanderThemeSettings,
} from "features/lightningLanders/providers/LightningLanderThemeProvider";
import { isEmpty } from "Helpers/isEmpty";
import { useLanderLandingComponents } from "features/lightningLanders/hooks/useLanderLandingComponents";

function LightningComponentPageInner() {
  const { componentId } = useParams<{ componentId: string }>();
  const { selectedProject, projectSettings } = useSelectedProject();
  const { setComponents } = useLanderLandingComponents();
  const history = useHistory();
  const location = useLocation();
  const { component, setComponent } = useLightningLanderContext();
  const { setTheme: setCustomTheme } = useLightningLanderThemeSettings();
  const creationStarted = useRef(false);

  const onEditSaved = useCallback(
    (savedComponent: ApiSimpleComponent) => {
      setComponent(savedComponent);
      setComponents((components) =>
        produce(components, (draft) => {
          if (!savedComponent) return;
          const idx = draft.findIndex((d) => d.id === savedComponent?.id);
          if (idx >= 0) {
            draft[idx] = savedComponent;
          }
        })
      );
    },
    [setComponents, setComponent]
  );

  useEffect(() => {
    if (!selectedProject?.id || Boolean(component.id)) {
      return;
    }
    if (componentId === "new" && creationStarted.current) {
      return;
    }
    if (isEmpty(projectSettings)) {
      return;
    }

    if (componentId === "new") {
      creationStarted.current = true;
      const createComponent = async () => {
        const res = await saveComponent(selectedProject.id!, {
          name: `Component (${friendlyDateTime(new Date())})`,
          type: ApiComponentType.LANDING_COMPONENT,
          theme:
            projectSettings[ApiProjectSettingKey.LANDING_THEME] ||
            JSON.stringify(landerDefaultTheme),
        });
        setComponents((components) =>
          produce(components, (draft) => {
            draft.push(res.component!);
          })
        );
        const newPath = location.pathname.replace(
          /\/new$/,
          `/${res.component?.id}`
        );
        history.push(newPath);
      };
      createComponent();
    } else {
      const loadComponent = async () => {
        const cRes = await readComponent(
          selectedProject?.id as number,
          Number(componentId)
        );
        if (cRes.component?.theme) {
          setCustomTheme(
            JSON.parse(cRes.component?.theme || "{}")
          );
        }
        setComponent(cRes.component as ApiSimpleComponent);
      };
      loadComponent();
    }
  }, [
    selectedProject,
    history,
    location,
    componentId,
    setComponent,
    setComponents,
    component.id,
    setCustomTheme,
    projectSettings,
  ]);

  if (!selectedProject || !component?.id) {
    return (
      <CenterPage calcHeight>
        <Loading />
      </CenterPage>
    );
  }

  return (
    <LightningLandersBuilder
      saveToken={null}
      project={selectedProject}
      onSaved={onEditSaved}
      componentMode={true}
    />
  );
}

function LightningComponentPage() {
  return (
    <LightningLanderProvider>
      <LightningLanderThemeProvider>
        <LightningComponentPageInner />
      </LightningLanderThemeProvider>
    </LightningLanderProvider>
  );
}

export default LightningComponentPage;
